import type { LeafletProcessingStatusApiModel } from '@/api/leaflets'

import type { CreationApi } from './creationApi'
import { errorMessage, uncertainResponse } from './creationErrors'
import { copyTab, CREATION_STEPS, type CreationStep, type CreationTask, hasFiles } from './creationTask'

export function createTaskExecutor({
  api,
  save,
  isActive
}: {
  api: CreationApi
  save: (task: CreationTask) => void
  isActive: (task: CreationTask) => boolean
}) {
  function stopWith(task: CreationTask, state: 'attention' | 'files' | 'unknown', error = '') {
    task.state = state
    task.error = error
    save(task)
  }

  async function recoverCreate(task: CreationTask): Promise<boolean> {
    const id = await api.find(task)
    if (!id) {
      stopWith(task, 'unknown', 'createUnconfirmed')
      return false
    }
    task.leafletId = id
    task.steps.create = 'done'
    // URL-based content is processed synchronously as part of creating the record.
    if (!task.tab.fileList.length) task.steps.generate = 'pending'
    save(task)
    return true
  }

  function acceptStatus(task: CreationTask, status?: LeafletProcessingStatusApiModel) {
    if (!status || status.leafletId !== task.leafletId) {
      stopWith(task, 'unknown', 'statusUnavailable')
      return
    }
    if (status.status === 'PENDING' && !task.uploadAccepted && task.steps.upload !== 'done') {
      stopWith(task, 'unknown', 'uploadUnconfirmed')
      return
    }
    if (task.steps.upload !== 'done') task.steps.upload = 'done'
    if (status.status === 'READY') {
      task.steps.generate = 'done'
      task.state = 'queued'
    } else if (status.status === 'FAILED') {
      task.steps.generate = 'failed'
      stopWith(task, 'attention', status.error || 'generationFailed')
      return
    } else {
      task.state = 'generating'
    }
    save(task)
  }

  async function recoverInterruptedStep(task: CreationTask): Promise<boolean> {
    if (['running', 'uncertain'].includes(task.steps.create)) return recoverCreate(task)
    if (['running', 'uncertain'].includes(task.steps.upload)) {
      const statuses = await api.statuses([task.leafletId!])
      acceptStatus(
        task,
        statuses.find(status => status.leafletId === task.leafletId)
      )
      return task.state !== 'unknown' && task.state !== 'attention'
    }
    for (const step of ['update', 'cover', 'schedule', 'finish'] as const) {
      if (task.steps[step] === 'running' || task.steps[step] === 'uncertain') task.steps[step] = 'pending'
    }
    return true
  }

  async function perform(task: CreationTask, step: CreationStep) {
    task.step = step
    task.state = 'running'
    task.error = ''
    task.steps[step] = 'running'
    save(task) // Checkpoint before issuing a mutation whose response could be lost.
    try {
      if (step === 'create') task.leafletId = (await api.create(task)).id
      else if (step === 'update') {
        await api.update(task)
        task.originalNaturalId = task.tab.naturalId
      } else if (step === 'upload') {
        const status = await api.upload(task)
        if (
          status.leafletId !== task.leafletId ||
          !['PENDING', 'PROCESSING', 'READY', 'FAILED'].includes(status.status)
        ) {
          throw new Error('uploadUnconfirmed')
        }
        task.uploadAccepted = true
        task.steps.upload = 'done'
        acceptStatus(task, status)
        return
      } else if (step === 'cover') await api.cover(task)
      else if (step === 'schedule') await api.schedule(task)
      else if (step === 'finish') await api.finish(task)
      task.steps[step] = 'done'
      if (step === 'create') task.originalNaturalId = task.tab.naturalId
      save(task)
    } catch (error) {
      task.steps[step] = uncertainResponse(error) ? 'uncertain' : 'failed'
      stopWith(task, task.steps[step] === 'uncertain' ? 'unknown' : 'attention', errorMessage(error))
    }
  }

  async function execute(task: CreationTask) {
    if (!(await recoverInterruptedStep(task))) return
    const order: CreationStep[] = ['create', 'update', 'schedule', 'upload', 'generate', 'cover', 'finish']
    for (const step of order) {
      if (!isActive(task)) return
      if (task.steps[step] === 'done') continue
      task.step = step
      if (step === 'generate') {
        task.state = 'generating'
        save(task)
        return
      }
      if (
        (step === 'upload' && !hasFiles(task.tab.fileList)) ||
        (step === 'cover' && !hasFiles(task.tab.firstPageFile))
      ) {
        stopWith(task, 'files', 'filesRequired')
        return
      }
      await perform(task, step)
      if (['unknown', 'attention', 'files'].includes(task.state)) return
    }
    task.state = 'done'
    task.error = ''
    task.completedAt = Date.now()
    // Keep file descriptors for history; release potentially large local blobs.
    task.tab = copyTab(task.tab, false)
    save(task)
  }

  async function prepareRetry(task: CreationTask) {
    if (task.steps.generate === 'failed') {
      try {
        const statuses = await api.statuses([task.leafletId!])
        const status = statuses.find(status => status.leafletId === task.leafletId)
        if (!status || status.status === 'PENDING') {
          stopWith(task, 'unknown', 'uploadUnconfirmed')
          return
        }
        if (status.status !== 'FAILED') {
          acceptStatus(task, status)
          return
        }
      } catch {
        stopWith(task, 'unknown', 'statusUnavailable')
        return
      }
      const localFiles = task.tab.fileList.length > 0
      task.steps.upload = localFiles ? 'pending' : 'done'
      task.steps.generate = localFiles ? 'pending' : 'done'
      if (!localFiles) task.steps.update = 'pending'
      task.uploadAccepted = false
      task.step = localFiles ? 'upload' : 'update'
    }
    for (const step of CREATION_STEPS) {
      if (task.steps[step] === 'failed') task.steps[step] = 'pending'
    }
    task.state = task.step === 'generate' && task.steps.upload === 'done' ? 'generating' : 'queued'
    task.error = ''
    save(task)
  }

  return { execute, acceptStatus, stopWith, prepareRetry }
}
